/**
 * Platform timezone utilities.
 *
 * Provides helpers for working with the platform's configured timezone
 * (from LOCALIST_SETTINGS.time_zone.identifier). Used by the event schedule
 * (instance manager) and ticket availability date/time fields.
 */

const getPlatformTimeZoneId = () =>
  (typeof LOCALIST_SETTINGS !== 'undefined') ? LOCALIST_SETTINGS?.time_zone?.identifier : null;

/**
 * Returns the short generic timezone label for the platform timezone
 * (e.g., "ET", "PT", "CT"). Returns null if unavailable.
 */
const getPlatformTimeZoneName = () => {
  const tz = getPlatformTimeZoneId();
  if (!tz) return null;

  try {
    const formatter = new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      timeZoneName: 'shortGeneric',
    });
    const parts = formatter.formatToParts(new Date());
    return parts.find((part) => part.type === 'timeZoneName').value;
  } catch {
    return null;
  }
};

/**
 * Extracts date/time parts from a Date object as they appear in the platform timezone.
 *
 * @param {Date} date - The date to extract parts from
 * @returns {{ year: number, month: number, day: number, hour: number, minute: number } | null}
 *   month is 1-based (1–12). hour uses 24h format (0–23). Returns null if unavailable.
 */
const getDatePartsInPlatformTZ = (date) => {
  const tz = getPlatformTimeZoneId();
  if (!tz || !(date instanceof Date)) return null;

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const result = {};
    formatter.formatToParts(date).forEach(({ type, value }) => {
      result[type] = parseInt(value, 10);
    });
    if (result.hour === 24) result.hour = 0;
    return result;
  } catch {
    return null;
  }
};

/**
 * Returns the platform timezone's UTC offset in milliseconds at the given date.
 * Positive values mean east of UTC (ahead), negative means west (behind).
 *
 * @param {Date} date - The reference date (offset varies with DST)
 * @returns {number} Offset in milliseconds (0 if timezone unavailable)
 */
const getPlatformTZOffsetMs = (date) => {
  const tz = getPlatformTimeZoneId();
  if (!tz) return 0;

  try {
    const utcStr = date.toLocaleString('en-US', { timeZone: 'UTC' });
    const tzStr = date.toLocaleString('en-US', { timeZone: tz });
    return new Date(tzStr).getTime() - new Date(utcStr).getTime();
  } catch {
    return 0;
  }
};

/**
 * Builds a UTC ISO 8601 string from calendar date/time components that are
 * expressed in the platform timezone.
 *
 * For example, if the platform timezone is America/New_York (UTC-4 in EDT)
 * and you pass (2026, 2, 23, 18, 0) meaning "March 23, 2026 6:00 PM EDT",
 * the returned ISO string will be "2026-03-23T22:00:00.000Z".
 *
 * @param {number} year  - Full year
 * @param {number} month - 0-based month (0 = January)
 * @param {number} day   - Day of month
 * @param {number} [hours=0]   - Hours (0–23)
 * @param {number} [minutes=0] - Minutes (0–59)
 * @returns {string} ISO 8601 UTC string
 */
const buildISOStringInPlatformTZ = (year, month, day, hours = 0, minutes = 0) => {
  const utcGuess = new Date(Date.UTC(year, month, day, hours, minutes, 0));
  const offset1 = getPlatformTZOffsetMs(utcGuess);
  const utc1 = new Date(utcGuess.getTime() - offset1);
  const offset2 = getPlatformTZOffsetMs(utc1);
  if (offset1 === offset2) {
    return utc1.toISOString();
  }
  return new Date(utcGuess.getTime() - offset2).toISOString();
};

export {
  getPlatformTimeZoneId,
  getPlatformTimeZoneName,
  getDatePartsInPlatformTZ,
  getPlatformTZOffsetMs,
  buildISOStringInPlatformTZ,
};
